import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as AuthenticationActions from '@store/actions/authentication.action';
import * as StorageActions from '@store/actions/storage.action';
import * as UserActions from '@store/actions/user.action';
import {concatMap, map, tap} from 'rxjs/operators';
import {AuthenticationService} from '@iw-shop/core/services/authentication.service';
import {AppState} from '@store/state/app.state';
import {Store} from '@ngrx/store';
import {resetUser} from '@store/actions/user.action';
import {resetTokensExpire, setAccessExpire, setRefreshExpire, userId} from '@store/actions/storage.action';

@Injectable()
export class AuthenticationEffects {
    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private authenticationService: AuthenticationService) {
    }

    login$ = createEffect(() => this.actions$.pipe(
            ofType(AuthenticationActions.login),
            concatMap((action) => this.authenticationService.login(action.username, action.password)
                .pipe(
                    map((r) => {
                        if (r.result?.userTokens.data.expiredIn) {
                            this.authenticationService.setAccessTokenCookie();
                            return AuthenticationActions.loginSuccess(
                                {
                                    userId: r.result?.userTokens.data.userId,
                                    accessExpiredIn: r.result?.userTokens.data.expiredIn
                                }
                            )
                        }
                        this.authenticationService.deleteAccessTokenCookie();
                        return AuthenticationActions.loginFailure(
                            {errorMsg: r.error?.message || '[login] Es ist ein unbekannter Fehler aufgetreten.'}
                        )
                    })
                )
            )
        )
    );

    loginSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(AuthenticationActions.loginSuccess),
        map((action) => {
                this.store.dispatch(setAccessExpire({expiredIn: action.accessExpiredIn}));
                this.store.dispatch(setRefreshExpire({expiredIn: 60 * 60 * 24 * 29}));
                return UserActions.setUser({userId: action.userId});
            }
        )
    ));

    refreshToken$ = createEffect(() => this.actions$.pipe(
        ofType(AuthenticationActions.refreshToken),
        concatMap(() => this.authenticationService.refreshToken()
            .pipe(
                map((r) => {
                    if (r.result) {
                        return AuthenticationActions.refreshTokenSuccess(
                            {
                                accessExpiredIn: r.result?.userTokens.data.expiredIn
                            }
                        )
                    } else {
                        this.authenticationService.deleteAccessTokenCookie();
                        return AuthenticationActions.refreshTokenFailure()
                    }
                })
            )
        )
    ));

    refreshSuccess = createEffect(() => this.actions$.pipe(
        ofType(AuthenticationActions.refreshTokenSuccess),
        map((action) => {
                this.store.dispatch(setAccessExpire({expiredIn: action.accessExpiredIn}));
                return StorageActions.setRefreshExpire({expiredIn: 60 * 60 * 24 * 29});
            }
        )
    ));

    refreshTokenFailure = createEffect(() => this.actions$.pipe(
        ofType(AuthenticationActions.refreshTokenFailure),
        map(() => {
                return AuthenticationActions.logout();
            }
        )
    ));

    logout$ = createEffect(() => this.actions$.pipe(
            ofType(AuthenticationActions.logout),
            tap(() => {
                    this.store.dispatch(resetTokensExpire());
                    this.store.dispatch(userId({userId: null}));
                }
            ),
            concatMap(() => this.authenticationService.logout()
                .pipe(
                    map(r => {
                        this.authenticationService.deleteAccessTokenCookie();
                        if (r.result?.userTokens.deleted) {
                            return AuthenticationActions.logoutSuccess();
                        }
                        return AuthenticationActions.logoutFailure();
                    })
                ))
        )
    );

    logoutSuccess$ = createEffect(() => this.actions$.pipe(
            ofType(AuthenticationActions.logoutSuccess),
            tap(() => {
                    this.store.dispatch(resetUser());
                    /* this.router.navigate(['/']).then();*/
                }
            )
        ), {dispatch: false}
    );
}
